import React, { useEffect, useMemo, useState } from 'react';
import SemanticTableFormDialog from '../semantic-table/SemanticTableFormDialog';
import SemanticTableService from '../../services/SemanticTableService';
import SemanticDatabaseService from '../../services/SemanticDatabaseService';
import UserProfileService from '../../services/UserProfileService';
import { apiErrorMessage } from './modelGraphData';

const resolveAccountId = (profile) => profile?.account_id || profile?.accountUuid || profile?.account_uuid;
const resolveUserId = (profile) => profile?.user_id || profile?.userId || profile?.user_uuid;

const asList = (response) => {
  if (Array.isArray(response)) return response;
  return response?.items || response?.data || [];
};

const asTableRecord = (response) => {
  if (!response) return null;
  if (response.table_id) return response;
  if (response.data) return asTableRecord(response.data);
  if (response.item) return asTableRecord(response.item);
  return null;
};

// Bases semánticas, conexiones y perfil: se cargan una sola vez, la primera
// vez que se abre el formulario (no hacen falta para dibujar el diagrama).
let auxDataPromise = null;
const loadAuxData = () => {
  if (!auxDataPromise) {
    auxDataPromise = (async () => {
      const profile = await UserProfileService.getProfile();
      const accountId = resolveAccountId(profile);
      const userId = resolveUserId(profile);
      const [databases, connections] = await Promise.all([
        accountId ? SemanticDatabaseService.list(accountId, { includeDeleted: false }).catch(() => []) : [],
        userId ? SemanticDatabaseService.listConnections(userId).catch(() => []) : []
      ]);
      return {
        accountId,
        accountName: profile?.company_name || profile?.account_name || '',
        databases: asList(databases),
        connections: connections || []
      };
    })().catch((error) => {
      auxDataPromise = null; // reintentar en la próxima apertura
      throw error;
    });
  }
  return auxDataPromise;
};

export const resetEntityEditCache = () => {
  auxDataPromise = null;
};

/**
 * Edita una entidad (tabla semántica) desde el diagrama con el MISMO
 * formulario que la pantalla Entidades (SemanticTableFormDialog), y el
 * mismo guardado (SemanticTableService.update con account_id + table_id).
 *
 * El formulario envía también los `joins` de la tabla y el backend
 * reemplaza la lista completa: por eso se edita la versión fresca del
 * servidor (getById), que incluye las relaciones creadas en el diagrama.
 *
 * `onSaved(updatedTable, previousTable)` recibe la tabla guardada.
 */
const EntityEditDialog = ({ table, onClose, onSaved, onError }) => {
  const [aux, setAux] = useState(null);
  const [record, setRecord] = useState(table);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setRecord(table);
    if (!table) return undefined;
    loadAuxData()
      .then((data) => { if (!cancelled) setAux(data); })
      .catch((error) => {
        if (!cancelled) onError?.(apiErrorMessage(error, 'No se pudieron cargar los datos del formulario.'));
      });
    SemanticTableService.getById(table.table_id)
      .then((fresh) => {
        const hydrated = asTableRecord(fresh);
        if (!cancelled && hydrated) setRecord(hydrated);
      })
      .catch(() => {
        // Se edita la versión que ya tenía el diagrama.
      });
    return () => {
      cancelled = true;
    };
    // Solo al abrir otra entidad, no en cada render del diagrama.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table?.table_id]);

  // El formulario reinicia sus campos cuando cambian estas props (ver su
  // useEffect): tienen que ser estables entre renders del diagrama o se
  // borraría lo que el usuario está escribiendo.
  const EMPTY = useMemo(() => [], []);
  const accountInfo = useMemo(
    () => ({ id: aux?.accountId, name: aux?.accountName || '' }),
    [aux?.accountId, aux?.accountName]
  );

  const handleSubmit = async (formPayload) => {
    setIsSaving(true);
    try {
      const accountId = aux?.accountId || record.account_id;
      const payload = { ...formPayload, account_id: accountId, table_id: record.table_id };
      const response = await SemanticTableService.update(record.table_id, payload);
      if (response === null) {
        throw new Error('La entidad ya no existe. Recarga el diagrama.');
      }
      const updated = { ...record, ...payload, ...(asTableRecord(response) || {}) };
      await onSaved(updated, record);
    } catch (error) {
      onError?.(apiErrorMessage(error, 'No se pudo guardar la entidad.'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <SemanticTableFormDialog
      open={Boolean(table)}
      mode="edit"
      initialValues={record}
      databases={aux?.databases || EMPTY}
      connections={aux?.connections || EMPTY}
      onClose={() => !isSaving && onClose()}
      onSubmit={handleSubmit}
      isSaving={isSaving}
      accountInfo={accountInfo}
    />
  );
};

export default EntityEditDialog;
